/**
 * Navigation Guard Context
 * 
 * Provides navigation guard functionality for pages with unsaved changes
 * Allows child components to check if navigation should be blocked
 */

import React, { createContext, useContext, ReactNode } from 'react';

interface NavigationGuardContextType {
  hasUnsavedChanges: boolean;
  onNavigationAttempt: (path: string) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextType | undefined>(undefined);

interface NavigationGuardProviderProps {
  children: ReactNode;
  hasUnsavedChanges: boolean;
  onNavigationAttempt: (path: string) => void;
}

export function NavigationGuardProvider({
  children,
  hasUnsavedChanges,
  onNavigationAttempt,
}: NavigationGuardProviderProps): React.ReactElement {
  return (
    <NavigationGuardContext.Provider value={{ hasUnsavedChanges, onNavigationAttempt }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard(): NavigationGuardContextType {
  const context = useContext(NavigationGuardContext);
  // Return no-op if context not available (for pages without guard)
  if (context === undefined) {
    return {
      hasUnsavedChanges: false,
      onNavigationAttempt: () => {},
    };
  }
  return context;
}

